import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  MenuItem,
  Modal,
  Fade,
  Backdrop,
  FormControlLabel,
  Checkbox,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { layoutCSS } from "../component/common/commonStyle";
import { maskNumber } from "../component/common/Encryption";

import {
  useGetShortcodesQuery,
  useGetKeywordsByShortcodeQuery,
  useGetConfigDataQuery,
  useSaveSubkeywordResponseMutation,
  useFetchPrincipalEntitiesQuery,
  useFetchHeadersByPrincipalIdQuery,
  useGetTemplatesByPrincipalQuery,
} from "../store/services/missedCallApi";
import { useGetCustomerServiceQuery } from "../store/services/resellerApi";

/* ─────────────────────────────────────────────
   Styles
───────────────────────────────────────────── */
const tableCellStyle = {
  fontSize: "0.78rem",
  padding: "8px 12px",
  whiteSpace: "nowrap",
  borderRight: "1px solid #1b1b1bff",
};

const headerCellStyle = {
  ...tableCellStyle,
  fontWeight: 600,
  backgroundColor: "#1565c0",
  color: "#fff",
  borderBottom: "none",
  borderRight: "1px solid #1b1b1bff",
};

const longTextCellStyle = {
  ...tableCellStyle,
  maxWidth: "150px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderRight: "1px solid #1b1b1bff",
};

const inputStyle = {
  width: 150,
  "& .MuiInputBase-input": { fontSize: "0.78rem", padding: "6px 8px" },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
};

const modalTextFieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.78rem" },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#c4c4c4" },
  },
};

/* ─────────────────────────────────────────────
   Mask ID helper
───────────────────────────────────────────── */
const maskId = (id) => {
  if (!id) return "—";
  const idStr = String(id);
  if (idStr.length <= 6) return idStr;
  return idStr.substring(0, 4) + "****" + idStr.substring(idStr.length - 2);
};

/* ─────────────────────────────────────────────
   Section header helper (with enable toggle)
───────────────────────────────────────────── */
const SectionHeaderWithToggle = ({ label, enabled, onToggle }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 3,
          height: 16,
          bgcolor: "#1565c0",
          borderRadius: "2px",
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#333" }}>
        {label}
      </Typography>
    </Box>
    <FormControlLabel
      control={
        <Checkbox
          checked={enabled}
          onChange={onToggle}
          size="small"
          sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
        />
      }
      label={
        <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>
          Enable
        </Typography>
      }
      sx={{ mr: 0 }}
    />
  </Box>
);

/* ─────────────────────────────────────────────
   Plain section header (no toggle)
───────────────────────────────────────────── */
const SectionHeader = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box
      sx={{
        width: 3,
        height: 16,
        bgcolor: "#1565c0",
        borderRadius: "2px",
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#333" }}>
      {label}
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────
   Main component
───────────────────────────────────────────── */
const SortCode = () => {
  const { data: scData, isLoading: scLoading } = useGetShortcodesQuery();
  const { data: configData } = useGetConfigDataQuery();
  const { data: entityData } = useFetchPrincipalEntitiesQuery();
  const { data: customerServiceData } = useGetCustomerServiceQuery();

  // Check if email service is active for this customer
  const isEmailServiceActive = customerServiceData?.data?.some(
    (s) =>
      s.service?.service_name?.toUpperCase() === "EMAIL" &&
      s.service?.isactive === true &&
      s.show_service === true
  );

  const [selectedShortcode, setSelectedShortcode] = useState("");
  const [selectedKeywordId, setSelectedKeywordId] = useState("");

  const {
    data: keywordsData,
    isLoading: keywordsLoading,
    refetch,
  } = useGetKeywordsByShortcodeQuery(selectedShortcode, {
    skip: !selectedShortcode,
  });

  const [saveResponse, { isLoading: isSaving }] =
    useSaveSubkeywordResponseMutation();

  const [open, setOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isAlertTemplate, setIsAlertTemplate] = useState(false);
  const [isResponseTemplate, setIsResponseTemplate] = useState(false);

  const [form, setForm] = useState({
    subkeyword_id: "",
    keyword_id: "",
    keyword: "",
    shortcode: "",
    forward_to_email: "",
    // Alert (SMS) section
    enable_sms_alert: false,
    alert_entity_id: "",
    alert_sender_id: "",
    alert_template_id: "",
    alert_template_text: "",
    alert_template_trai_id: "",
    alert_template_type: "",
    forward_to_mobile: "",
    // Reply (SMS) section – now with separate entity/sender
    enable_reply_msg: false,
    reply_entity_id: "",          // NEW
    reply_sender_id: "",          // NEW
    response_template_id: "",
    response_text: "",
    restemplate_id: "",
    // Email alert section
    enable_email_alert: false,
    // Common
    is_active: true,
  });

  /* ── Alert section: fetch headers & templates based on alert_entity_id's principal ── */
  const { data: alertHeadersData } = useFetchHeadersByPrincipalIdQuery(
    (() => {
      const selectedAlertEntity = entityData?.data?.find(
        (e) => `${e.pe_id}` === `${form.alert_entity_id}`
      );
      return selectedAlertEntity?.principalentity_id || 0;
    })(),
    { skip: !form.alert_entity_id }
  );

  const { data: alertTemplatesData } = useGetTemplatesByPrincipalQuery(
    (() => {
      const selectedAlertEntity = entityData?.data?.find(
        (e) => `${e.pe_id}` === `${form.alert_entity_id}`
      );
      return selectedAlertEntity?.principalentity_id || 0;
    })(),
    { skip: !form.alert_entity_id }
  );

  /* ── Reply section: fetch headers & templates based on reply_entity_id's principal ── */
  const { data: replyHeadersData } = useFetchHeadersByPrincipalIdQuery(
    (() => {
      const selectedReplyEntity = entityData?.data?.find(
        (e) => `${e.pe_id}` === `${form.reply_entity_id}`
      );
      return selectedReplyEntity?.principalentity_id || 0;
    })(),
    { skip: !form.reply_entity_id }
  );

  const { data: replyTemplatesData } = useGetTemplatesByPrincipalQuery(
    (() => {
      const selectedReplyEntity = entityData?.data?.find(
        (e) => `${e.pe_id}` === `${form.reply_entity_id}`
      );
      return selectedReplyEntity?.principalentity_id || 0;
    })(),
    { skip: !form.reply_entity_id }
  );

  const alertTemplates = alertTemplatesData?.data || [];
  const replyTemplates = replyTemplatesData?.data || [];

  /* ── Handlers ── */
  const handleAlertEntityChange = (peId) => {
    setForm((s) => ({
      ...s,
      alert_entity_id: peId,
      alert_sender_id: "",                // reset sender when entity changes
      alert_template_id: "",
      alert_template_text: "",
      alert_template_trai_id: "",
      alert_template_type: "",
    }));
  };

  // NEW: handler for reply entity change
  const handleReplyEntityChange = (peId) => {
    setForm((s) => ({
      ...s,
      reply_entity_id: peId,
      reply_sender_id: "",                // reset sender when entity changes
      response_template_id: "",
      response_text: "",
      restemplate_id: "",
    }));
  };

  const handleAlertTemplateSelect = (templateId) => {
    const temp = alertTemplates.find(
      (t) => `${t.template_id}` === `${templateId}`
    );
    if (!temp) return;
    setForm((s) => ({
      ...s,
      alert_template_id: temp.template_id,
      alert_template_text: temp.template_text,
      alert_template_trai_id: temp.trai_template_id,
      alert_template_type: temp.template_type,
    }));
  };

  const handleReplyTemplateSelect = (templateId) => {
    const temp = replyTemplates.find(
      (t) => `${t.template_id}` === `${templateId}`
    );
    if (!temp) return;
    setForm((s) => ({
      ...s,
      response_template_id: temp.template_id,
      response_text: temp.template_text,
      restemplate_id: temp.template_id,
    }));
  };

  const handleConfigureKeyword = (k) => {
    setForm({
      subkeyword_id: k.subkeyword_id || k.keyword_id || "",
      shortcode: selectedShortcode,
      keyword_id: k.keyword_id,
      keyword: k.keyword,
      forward_to_email: k.forward_to_email || "",
      // Alert
      enable_sms_alert: k.enable_sms_alert ?? false,
      alert_entity_id: k.entity_id ? `${k.entity_id}` : "",
      alert_sender_id: k.sender_id ? `${k.sender_id}` : "",
      alert_template_id: k.template_id || "",
      alert_template_text: k.template_text || "",
      alert_template_trai_id: k.template_trai_id || "",
      alert_template_type: k.template_type || "",
      forward_to_mobile: k.forward_to_mobile || "",
      // Reply – now with separate fields
      enable_reply_msg: k.enable_reply_msg ?? false,
      reply_entity_id: k.reply_entity_id ? `${k.reply_entity_id}` : "",
      reply_sender_id: k.reply_sender_id ? `${k.reply_sender_id}` : "",
      response_template_id: k.restemplate_id || "",
      response_text: k.response_text || "",
      restemplate_id: k.restemplate_id || "",
      // Email
      enable_email_alert: k.enable_email_alert ?? false,
      is_active: k.is_active ?? true,
    });
    setOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (
      form.enable_sms_alert &&
      (!form.alert_template_id || !form.alert_entity_id)
    ) {
      toast.error(
        "Please complete Alert Message configuration (Entity and Template)"
      );
      return;
    }

    if (form.enable_reply_msg && (!form.response_template_id || !form.reply_entity_id)) {
      toast.error("Please complete Reply Message configuration (Entity and Template)");
      return;
    }

    const payload = {
      subkeyword_id: form.subkeyword_id,
      shortcode: form.shortcode,
      keyword_id: form.keyword_id,

      // Alert (SMS)
      enable_sms_alert: form.enable_sms_alert,
      alert_enabled: form.enable_sms_alert,
      entity_id: form.alert_entity_id || null,
      sender_id: form.alert_sender_id || null,
      template_id: form.alert_template_id || null,
      template_text: form.alert_template_text || null,
      template_trai_id: form.alert_template_trai_id || null,
      template_type: form.alert_template_type || null,
      forward_to_mobile: form.forward_to_mobile || null,

      // Reply (SMS) – now using separate reply_entity_id and reply_sender_id
      enable_reply_msg: form.enable_reply_msg,
      response_enabled: form.enable_reply_msg,
      reply_entity_id: form.enable_reply_msg ? form.reply_entity_id || null : null,
      reply_sender_id: form.enable_reply_msg ? form.reply_sender_id || null : null,
      response_template_id: form.enable_reply_msg ? form.response_template_id : null,
      response_text: form.enable_reply_msg ? form.response_text : "",
      restemplate_id: form.enable_reply_msg ? form.restemplate_id : null,

      // Email
      enable_email_alert: form.enable_email_alert,
      forward_to_email: form.enable_email_alert ? form.forward_to_email : null,

      // Common
      is_active: form.is_active,
    };

    try {
      await saveResponse(payload).unwrap();
      toast.success("Configuration Updated Successfully!");
      refetch();
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || "Failed to update configuration");
    }
  };

  return (
    <Box sx={layoutCSS}>
      <Typography
        variant="h6"
        sx={{ mb: 2, fontSize: "0.95rem", fontWeight: "bold" }}
      >
        Short Code Configuration
      </Typography>

      {/* Selection Panel */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: "10px", mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <TextField
              select
              label="Shortcode"
              value={selectedShortcode}
              onChange={(e) => setSelectedShortcode(e.target.value)}
              size="small"
              sx={inputStyle}
            >
              <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                Select Shortcode
              </MenuItem>
              {scLoading ? (
                <MenuItem disabled>Loading...</MenuItem>
              ) : (
                scData?.data?.map((s) => (
                  <MenuItem
                    key={s.shortcode}
                    value={s.shortcode}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {s.shortcode}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Keywords Table */}
      <Paper elevation={1} sx={{ p: 2, borderRadius: "10px" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", mb: 1 }}>
          Keywords List
        </Typography>
        <TableContainer>
          <Table
            size="small"
            stickyHeader
            sx={{
              borderLeft: "1px solid #1b1b1bff",
              borderTop: "1px solid #1b1b1bff",
              borderBottom: "1px solid #1b1b1bff",
            }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Shortcode",
                  "Keyword",
                  "Email",
                  "Entity",
                  "Sender ID",
                  "R.Entity ID",
                  "R.Sender ID",
                  "Alert Mobile",
                  "Alert Message",
                  "Reply Message",
                  "Action",
                ].map((h) => (
                  <TableCell key={h} sx={headerCellStyle}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {keywordsLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : (
                keywordsData?.data?.map((k) => (
                  <TableRow
                    key={k.keyword_id}
                    hover
                    sx={{ "&:hover": { backgroundColor: "#f5f8ff" } }}
                  >
                    <TableCell sx={tableCellStyle}>
                      {selectedShortcode}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>{k.keyword}</TableCell>
                    <TableCell sx={longTextCellStyle}>
                      <Tooltip title={k.forward_to_email || ""}>
                        <span>{k.forward_to_email}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {maskNumber(k.entity_id)}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>{maskNumber(k.sender_id)}</TableCell>
                    <TableCell sx={tableCellStyle}>
                      {maskNumber(k.reply_entity_id)}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {maskNumber(k.reply_sender_id)}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {k.forward_to_mobile}
                    </TableCell>
                    <TableCell sx={longTextCellStyle}>
                      <Tooltip title={k.template_text || ""}>
                        <span>{k.template_text}</span>
                      </Tooltip>
                    </TableCell>
                   
                    <TableCell sx={longTextCellStyle}>
                      <Tooltip title={k.response_text || ""}>
                        <span>{k.response_text}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleConfigureKeyword(k)}
                        sx={{
                          fontSize: "0.72rem",
                          textTransform: "none",
                          bgcolor: "#1565c0",
                          "&:hover": { bgcolor: "#0d47a1" },
                        }}
                      >
                        Configure
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Configure Modal */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95vw", sm: 680 },
              bgcolor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
              overflow: "hidden",
              maxHeight: "92vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                bgcolor: "#1565c0",
                px: 2.5,
                py: 1.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                <Box>
                  <Typography
                    sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)" }}
                  >
                    Shortcode
                  </Typography>
                  <Typography
                    sx={{ fontSize: "1rem", fontWeight: 600, color: "#fff" }}
                  >
                    {form.shortcode || "—"}
                  </Typography>
                </Box>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "rgba(255,255,255,0.25)" }}
                />
                <Box>
                  <Typography
                    sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.7)" }}
                  >
                    Keyword
                  </Typography>
                  <TextField
                    select
                    value={form.keyword_id}
                    onChange={(e) => {
                      const keywordId = e.target.value;
                      setSelectedKeywordId(keywordId);
                      const keywordObj = keywordsData?.data?.find(
                        (k) => `${k.keyword_id}` === `${keywordId}`
                      );
                      setForm((s) => ({
                        ...s,
                        keyword_id: keywordId,
                        keyword: keywordObj?.keyword || "",
                      }));
                    }}
                    size="small"
                    variant="standard"
                    sx={{
                      minWidth: 110,
                      "& .MuiInput-input": {
                        color: "#fff",
                        fontSize: "1rem",
                        fontWeight: 600,
                        paddingBottom: 0,
                      },
                      "& .MuiSvgIcon-root": {
                        color: "rgba(255,255,255,0.8)",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "rgba(255,255,255,0.3)",
                      },
                      "& .MuiInput-underline:hover:before": {
                        borderBottomColor: "rgba(255,255,255,0.6)",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff",
                      },
                    }}
                  >
                    {keywordsData?.data?.map((k) => (
                      <MenuItem key={k.keyword_id} value={k.keyword_id}>
                        {k.keyword}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <IconButton
                onClick={() => setOpen(false)}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.15)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                  width: 32,
                  height: 32,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Scrollable Body */}
            <Box
              sx={{
                overflowY: "auto",
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {/* 1. Reply Message – now with Entity & Sender dropdowns */}
              <Box>
                <SectionHeaderWithToggle
                  label="Reply message (SMS)"
                  enabled={form.enable_reply_msg}
                  onToggle={(e) =>
                    setForm((s) => ({
                      ...s,
                      enable_reply_msg: e.target.checked,
                    }))
                  }
                />
                {form.enable_reply_msg && (
                  <Box
                    sx={{
                      bgcolor: "#f5f8ff",
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Entity"
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={form.reply_entity_id}
                          onChange={(e) =>
                            handleReplyEntityChange(e.target.value)
                          }
                          sx={modalTextFieldSx}
                        >
                          {entityData?.data?.map((item) => (
                            <MenuItem key={item.pe_id} value={item.pe_id}>
                              {item.pe_name} ({item.pe_id})
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Sender ID"
                          fullWidth
                          size="small"
                          variant="outlined"
                          name="reply_sender_id"
                          value={form.reply_sender_id}
                          onChange={handleChange}
                          sx={modalTextFieldSx}
                        >
                          {replyHeadersData?.data?.map((h) => (
                            <MenuItem key={h.header_id} value={h.header_id}>
                              {h.header_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#666", mb: 0.5 }}
                      >
                        Template
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "stretch" }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            border: "0.5px solid #c4c4c4",
                            borderRadius: "6px",
                            px: 1.5,
                            py: 0.8,
                            fontSize: "0.75rem",
                            color: form.response_text ? "#333" : "#999",
                            bgcolor: "#fff",
                            minHeight: 36,
                            lineHeight: 1.5,
                          }}
                        >
                          {form.response_text || "No reply template selected"}
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsAlertTemplate(false);
                            setIsResponseTemplate(true);
                            setIsTemplateModalOpen(true);
                          }}
                          sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            borderColor: "#1565c0",
                            color: "#1565c0",
                          }}
                        >
                          Choose
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* 2. Alert Message */}
              <Box>
                <SectionHeaderWithToggle
                  label="Alert message (SMS)"
                  enabled={form.enable_sms_alert}
                  onToggle={(e) =>
                    setForm((s) => ({
                      ...s,
                      enable_sms_alert: e.target.checked,
                    }))
                  }
                />
                {form.enable_sms_alert && (
                  <Box
                    sx={{
                      bgcolor: "#f5f8ff",
                      borderRadius: "8px",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {/* Forward Mobile Number */}
                    <TextField
                      label="Forward Mobile Number"
                      fullWidth
                      size="small"
                      variant="outlined"
                      name="forward_to_mobile"
                      value={form.forward_to_mobile}
                      onChange={handleChange}
                      placeholder="e.g. 9876543210"
                      sx={modalTextFieldSx}
                    />

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Entity"
                          fullWidth
                          size="small"
                          variant="outlined"
                          value={form.alert_entity_id}
                          onChange={(e) =>
                            handleAlertEntityChange(e.target.value)
                          }
                          sx={modalTextFieldSx}
                        >
                          {entityData?.data?.map((item) => (
                            <MenuItem key={item.pe_id} value={item.pe_id}>
                              {item.pe_name} ({item.pe_id})
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Sender ID"
                          fullWidth
                          size="small"
                          variant="outlined"
                          name="alert_sender_id"
                          value={form.alert_sender_id}
                          onChange={handleChange}
                          sx={modalTextFieldSx}
                        >
                          {alertHeadersData?.data?.map((h) => (
                            <MenuItem key={h.header_id} value={h.header_id}>
                              {h.header_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography
                        sx={{ fontSize: "0.72rem", color: "#666", mb: 0.5 }}
                      >
                        Template
                      </Typography>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "stretch" }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            border: "0.5px solid #c4c4c4",
                            borderRadius: "6px",
                            px: 1.5,
                            py: 0.8,
                            fontSize: "0.75rem",
                            color: form.alert_template_text ? "#333" : "#999",
                            bgcolor: "#fff",
                            minHeight: 36,
                            lineHeight: 1.5,
                          }}
                        >
                          {form.alert_template_text || "No template selected"}
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsAlertTemplate(true);
                            setIsResponseTemplate(false);
                            setIsTemplateModalOpen(true);
                          }}
                          sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            borderColor: "#1565c0",
                            color: "#1565c0",
                          }}
                        >
                          Choose
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* 3. Email Alert — only if email service active */}
              {isEmailServiceActive && (
                <Box>
                  <SectionHeaderWithToggle
                    label="Email alert"
                    enabled={form.enable_email_alert}
                    onToggle={(e) =>
                      setForm((s) => ({
                        ...s,
                        enable_email_alert: e.target.checked,
                      }))
                    }
                  />
                  {form.enable_email_alert && (
                    <Box
                      sx={{
                        bgcolor: "#f5f8ff",
                        borderRadius: "8px",
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        label="To"
                        fullWidth
                        size="small"
                        variant="outlined"
                        name="forward_to_email"
                        value={form.forward_to_email}
                        onChange={handleChange}
                        placeholder="alerts@example.com"
                        sx={modalTextFieldSx}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                borderTop: "1px solid #eee",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                bgcolor: "#fafafa",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.is_active}
                    name="is_active"
                    onChange={handleChange}
                    size="small"
                    sx={{ "& .MuiSvgIcon-root": { fontSize: 16 } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.75rem", color: "#555" }}>
                    Active
                  </Typography>
                }
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={() => setOpen(false)}
                  sx={{
                    fontSize: "0.78rem",
                    textTransform: "none",
                    color: "#555",
                    borderColor: "#ccc",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{
                    fontSize: "0.78rem",
                    textTransform: "none",
                    bgcolor: "#1565c0",
                    "&:hover": { bgcolor: "#0d47a1" },
                    minWidth: 72,
                  }}
                >
                  {isSaving ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Template Picker Dialog */}
      <Dialog
        open={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "0.85rem", fontWeight: 600, pb: 1 }}>
          Select Template
        </DialogTitle>
        <DialogContent sx={{ maxHeight: 350, overflowY: "auto", pt: 0 }}>
          {(isAlertTemplate ? alertTemplates : replyTemplates).length > 0 ? (
            <List dense>
              {(isAlertTemplate ? alertTemplates : replyTemplates).map(
                (template) => (
                  <ListItem
                    button
                    key={template.template_id}
                    onClick={() => {
                      if (isAlertTemplate)
                        handleAlertTemplateSelect(template.template_id);
                      else handleReplyTemplateSelect(template.template_id);
                      setIsTemplateModalOpen(false);
                    }}
                    sx={{
                      borderRadius: "6px",
                      mb: 0.5,
                      border: "0.5px solid #eee",
                      "&:hover": { bgcolor: "#f5f8ff" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          sx={{ fontSize: "0.78rem", fontWeight: 600 }}
                        >
                          {template.template_name}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.74rem",
                              display: "block",
                              color: "#555",
                            }}
                          >
                            {template.template_text}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{ fontSize: "0.7rem", color: "#999" }}
                          >
                            ID: {template.trai_template_id}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                )
              )}
            </List>
          ) : (
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "#999",
                py: 2,
                textAlign: "center",
              }}
            >
              No templates available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsTemplateModalOpen(false)}
            size="small"
            sx={{ fontSize: "0.78rem", textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SortCode;